import React, { useEffect } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './PrivacyPage.css';

const { Title, Text, Paragraph } = Typography;

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  // Set page metadata
  useEffect(() => {
    document.title = 'Privacy Policy | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Privacy Policy for SNS Intelligence. Learn how we collect, use, and protect your data when you join our waitlist or use our pharmaceutical market intelligence platform.');
  }, []);

  return (
    <div className="privacy-page">
      {/* Navigation */}
      <nav className="privacy-navbar">
        <div className="nav-container">
          <div
            className="nav-logo"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                navigate('/');
              }
            }}
          >
            <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="privacy-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="privacy-header">
              <Title level={1} className="privacy-title">
                Privacy Policy
              </Title>
              <Text className="privacy-subtitle">
                Last updated: {new Date().toLocaleDateString()}
              </Text>
            </div>

            <Card className="privacy-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph>
                  SNS Intelligence respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you interact with our website and services.
                </Paragraph>

                <div>
                  <Title level={3} className="section-title">
                    Information We Collect
                  </Title>
                  <Paragraph>
                    When you interact with our website or join our waitlist, we may collect:
                  </Paragraph>
                  <ul className="privacy-list">
                    <li><strong>Email address</strong> – to send product updates and communications</li>
                    <li><strong>Marketing consent</strong> – your agreement to receive communications from us</li>
                    <li><strong>Attribution data</strong> – UTM parameters, referrer URL, and landing page path to understand how users discover our platform</li>
                    <li><strong>Session information</strong> – a unique session identifier to track visits</li>
                    <li><strong>Technical data</strong> – browser user agent and a hashed IP address used for security, analytics, and abuse prevention</li>
                  </ul>
                  <Paragraph>
                    We do not store raw IP addresses.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    How We Use Your Information
                  </Title>
                  <Paragraph>
                    We use collected information to:
                  </Paragraph>
                  <ul className="privacy-list">
                    <li>Communicate product updates and platform information</li>
                    <li>Analyze marketing performance and user acquisition channels</li>
                    <li>Prevent spam, fraud, and abusive activity</li>
                    <li>Improve our website, services, and user experience</li>
                  </ul>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    Data Storage and Security
                  </Title>
                  <Paragraph>
                    We take appropriate technical and organizational measures to protect your information:
                  </Paragraph>
                  <ul className="privacy-list">
                    <li>Data is stored securely in our database</li>
                    <li>IP addresses are hashed before storage</li>
                    <li>Access to personal data is limited to authorized personnel only</li>
                    <li>We follow industry-standard security practices</li>
                  </ul>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    Data Retention
                  </Title>
                  <Paragraph>
                    We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    Your Rights
                  </Title>
                  <Paragraph>
                    Depending on your jurisdiction, you may have the right to:
                  </Paragraph>
                  <ul className="privacy-list">
                    <li>Request access to your personal data</li>
                    <li>Request correction or deletion of your data</li>
                    <li>Withdraw marketing consent at any time</li>
                  </ul>
                  <Paragraph>
                    To exercise these rights, please contact us using the information below.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    Changes to This Policy
                  </Title>
                  <Paragraph>
                    We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    Contact Us
                  </Title>
                  <Paragraph>
                    If you have questions about this Privacy Policy or how we handle your data:
                  </Paragraph>
                  <ul className="privacy-list">
                    <li><strong>Email:</strong> <a href="mailto:info@snsintel.com">info@snsintel.com</a></li>
                    <li><strong>Subject:</strong> Privacy Policy Inquiry</li>
                  </ul>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="privacy-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
